// The report as one document (summary, chapters A to E, sections) with each
// block's current text and review status, and the summary's own page.
// Selecting a statement opens its section, where the evidence and review live.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Download, FileDown, LayoutTemplate, Printer, Upload } from "lucide-react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import { useCurrentUser } from "../auth";
import type { ReportBlock } from "../api/types";
import { DraftTab, DraftText, FigureTable, ReviewBadge, figureTables } from "../components/drafting";
import { Menu, MenuItem } from "../components/menu";
import { ErrorState, Notice, PageHeader, SkeletonBlock } from "../components/ui";
import { keys, useDossier } from "../dossier";
import { formatDateTime, locale, w } from "../i18n";

type Filter = "all" | "to_review" | "approved" | "missing";
const FILTERS: Filter[] = ["all", "to_review", "approved", "missing"];

function blockPath(dossierId: string, code: string) {
  if (code === "SYN") return `/dossiers/${dossierId}/synthese`;
  return code.includes(".") ? `/dossiers/${dossierId}/sections/${code}` : `/dossiers/${dossierId}/chapters/${code}`;
}

/** Drafts often open with their own heading ("### A.2 Résultats..."): the report already shows it. */
function withoutTitle(text: string, title: string) {
  const lines = text.split("\n");
  const first = lines.findIndex(line => line.trim());
  const heading = first >= 0 ? lines[first].trim().match(/^#{1,6}\s*(.+)$/) : null;
  if (heading && heading[1].toLowerCase().includes(title.toLowerCase())) lines.splice(first, 1);
  return lines.join("\n");
}

function matches(block: ReportBlock, filter: Filter) {
  if (filter === "approved") return block.status === "approved";
  if (filter === "missing") return !block.text;
  if (filter === "to_review") return !!block.text && block.status !== "approved";
  return true;
}

/** The company's Word template used by the Word export (styles, headers, footers). */
function TemplateBar() {
  const { dossier } = useDossier();
  const { can } = useCurrentUser();
  const template = useResource(`report-template:${dossier.id}`, () => api.reportTemplate(dossier.id));
  const upload = useMutation(api.uploadReportTemplate);
  const remove = useMutation(api.deleteReportTemplate);
  const refresh = () => invalidate(`report-template:${dossier.id}`, keys.audit(dossier.id));
  const present = template.data?.present;
  return <div className="ws-card ws-card-pad ws-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
    <span className="ws-small">{present
      ? w("report.templateOn", { date: formatDateTime(template.data?.updated_at) })
      : w("report.templateOff")}</span>
    {can("approver") && <span className="ws-row">
      <label className="ws-btn sm"><Upload size={14} />{w(present ? "report.templateReplace" : "report.templateUpload")}
        <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" hidden
          onChange={async e => { const file = e.target.files?.[0]; e.target.value = ""; if (file && await upload.run(dossier.id, file)) refresh(); }} /></label>
      {present && <button className="ws-btn sm ghost" disabled={remove.pending}
        onClick={async () => { if (await remove.run(dossier.id)) refresh(); }}>{w("report.templateRemove")}</button>}
    </span>}
    {(upload.error || remove.error) && <div style={{ width: "100%" }}><ErrorState title={w("report.templateError")} error={upload.error || remove.error} /></div>}
  </div>;
}

export function ReportPage() {
  const { dossier } = useDossier();
  const navigate = useNavigate();
  const lang = locale();
  const report = useResource(keys.report(dossier.id, lang), () => api.report(dossier.id, lang));
  const [filter, setFilter] = useState<Filter>("all");
  const data = report.data;
  const blocks = (data?.blocks || []).filter(block => matches(block, filter));

  return <>
    <PageHeader eyebrow={`${dossier.report_type} ${dossier.reporting_year}`} title={w("report.title")} lead={w("report.lead")}
      actions={<div className="ws-row">
        <Link className="ws-btn" to={`/dossiers/${dossier.id}/report/template`}>
          <LayoutTemplate size={16} aria-hidden="true" />{w("report.referenceTemplate")}</Link>
        <span className="ws-split">
          <a className="ws-btn primary" href={api.reportExportUrl(dossier.id, lang)} download>
            <Download size={16} aria-hidden="true" />{w("report.export")}</a>
          <Menu label={w("report.moreExports")} trigger={<button type="button" className="ws-btn primary" aria-label={w("report.moreExports")}>
            <ChevronDown size={16} aria-hidden="true" /></button>}>
            <MenuItem asChild><a href={api.reportPdfUrl(dossier.id, lang)} download>
              <FileDown size={16} aria-hidden="true" />{w("report.exportPdf")}</a></MenuItem>
            <MenuItem onSelect={() => { setFilter("all"); window.setTimeout(() => window.print(), 50); }}>
              <Printer size={16} aria-hidden="true" />{w("report.print")}</MenuItem>
          </Menu></span></div>} />
    <TemplateBar />
    {report.error && <ErrorState title={w("common.genericError")} error={report.error} onRetry={report.reload} />}
    {data && <section className="ws-card ws-steps" aria-label={w("report.progress")}>
      {([["drafted", data.counts.drafted], ["approved", data.counts.approved], ["changes", data.counts.changes_requested]] as const)
        .map(([key, value]) => <div key={key} className="ws-step">
          <div className="ws-step-label">{w(`report.kpi.${key}`)}</div>
          <div className="ws-step-value">{value}<span className="ws-faint" style={{ fontSize: 14 }}> / {data.counts.total}</span></div>
          <div className="ws-bar" aria-hidden="true"><span style={{ width: `${Math.round(value / data.counts.total * 100)}%` }} /></div>
        </div>)}
      <div className="ws-step"><div className="ws-step-label">{w("report.kpi.export")}</div>
        <div className="ws-step-sub" style={{ marginTop: 6 }}>{w("report.exportNote")}</div></div>
    </section>}

    <div className="ws-row" style={{ margin: "16px 0 12px" }}>
      <label className="ws-small ws-muted" htmlFor="f-report">{w("report.filter")}</label>
      <select id="f-report" className="ws-select" value={filter} onChange={e => setFilter(e.target.value as Filter)}>
        {FILTERS.map(f => <option key={f} value={f}>{w(`report.filter.${f}`)}</option>)}</select>
    </div>

    {report.loading && <SkeletonBlock lines={10} />}
    {data && <article className="ws-card ws-report">
      <header className="ws-print-only ws-print-title"><h1>{data.entity_name}</h1>
        <p>{data.report_type} {data.reporting_year} · {w("report.printStatus", { approved: data.counts.approved, total: data.counts.total })}</p></header>
      {blocks.map(block => <section key={block.code} className={`ws-report-block level-${block.level}`}>
        <div className="ws-report-head">
          {block.level === 1
            ? <h2>{block.code === "SYN" ? block.title : `${block.code}. ${block.title}`}</h2>
            : <h3><span className="ws-mono">{block.code}</span> {block.title}</h3>}
          <span className="ws-row">
            {block.text && <ReviewBadge status={block.status} />}
            <Link className="ws-btn sm" to={blockPath(dossier.id, block.code)}>{w("common.open")}</Link>
          </span>
        </div>
        {block.text && block.status !== "approved" && <p className="ws-print-only ws-small" style={{ margin: "0 0 6px" }}>{w("report.printDraft")}</p>}
        {block.status === "approved" && block.approved_by && <p className="ws-small ws-muted" style={{ margin: "0 0 6px" }}>
          {w("report.approvedBy", { actor: block.approved_by, date: formatDateTime(block.approved_at) })}</p>}
        {block.text && figureTables(block.tables, block.table).map(table => <FigureTable key={table.id} table={table} />)}
        {block.text
          ? <DraftText text={withoutTitle(block.text, block.title)} citations={block.citations} onSelect={() => navigate(blockPath(dossier.id, block.code))} />
          : <p className="ws-muted ws-small" style={{ margin: 0 }}>{w("report.notDrafted")}</p>}
      </section>)}
      {!blocks.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w("report.emptyFilter")}</p>}
    </article>}
    <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("report.note")}</Notice></div>
  </>;
}

export function SynthesisPage() {
  const { dossier } = useDossier();
  return <>
    <PageHeader eyebrow={w("synthesis.eyebrow")} title={w("synthesis.title")} lead={w("synthesis.lead")}
      actions={<Link className="ws-btn" to={`/dossiers/${dossier.id}/report`}>{w("synthesis.openReport")}</Link>} />
    <DraftTab code="SYN" />
  </>;
}
